/*
 * Author: Dw
 * File Created: Thursday, 24-07-11 10:21 am
 * Last Modified: Wednesday, 25-03-26 09:37 am, by Harper Yan
 */

import { atobu, btoa, btoau } from 'b2a';
import qs from 'qs';

import { isClient } from '.';

interface Params {
  [key: string]: any;
}

const DOT = '.';

/**
 * Check is valid url
 * @param { string } url Target url
 * @returns { boolean } return boolean
 */
export const isValidUrl = (url: string): boolean => {
  try {
    return !!new URL(url);
  } catch {
    return false;
  }
};

export function getLevelDomain(
  level = 2,
  req: { hostname?: string } = {}
): string {
  const { hostname = '' } = isClient ? window.location : req;
  const domains = hostname.split('.');
  return domains.length > 2 ? domains.slice(-level).join(DOT) : hostname;
}

/**
 * 检查字符串是否为有效的 Base64 编码
 * @param str 要检查的字符串
 * @returns 如果是有效的 Base64 编码则返回 true，否则返回 false
 */
export const isBase64 = (str = ''): boolean => {
  if (str === '' || str.trim() === '') {
    return false;
  }

  try {
    // 尝试两种情况:
    // 1. 标准 Base64 格式 (btoau(atobu(str)) === str)
    // 2. URL 安全的 Base64 格式 (btoa(atobu(str)) === str)
    return btoau(atobu(str)) === str || btoa(atobu(str)) === str;
  } catch (err) {
    return false;
  }
};

/** base64 to str */
export const a2b = (str = ''): string => (isBase64(str) ? atobu(str) : str);

/** str to base64 */
export const b2a = (str = ''): string => (isBase64(str) ? str : btoau(str));

/**
 * Object to string
 * @param { object } queryObj Target object
 * @returns { string } query string
 */
export const query2Str = (queryObj: Record<string, unknown> = {}): string =>
  qs.stringify(queryObj);

/**
 * Get url params
 * @param { string } url Target url
 * @returns { qs.ParsedQs } result
 */
export const parseQuery = (url: string): qs.ParsedQs => {
  const newUrl = isValidUrl(url)
    ? new URL(url).search.slice(1)
    : url?.split?.('?')?.[1];
  return !newUrl ? {} : qs.parse(newUrl);
};

export const qsParse = qs.parse;

/**
 * Get query value by key
 * @param url Target url
 * @param key key of params
 * @returns Query parameter value or empty string if not found
 */
export const getUrlParam = (
  url: string,
  key: string
): string | qs.ParsedQs | (string | qs.ParsedQs)[] | '' => {
  return parseQuery(url)[key] ?? '';
};

/**
 * connect pathname and stringify search object to a new url
 * @param { string } path pathname OR url
 * @param { object } queryObj object for search params
 * @returns { string } new url
 */
export const connectUrl = (
  path: string,
  queryObj?: Record<string, unknown>
): string => {
  const { pathname, origin = '' } = isValidUrl(path)
    ? new URL(path)
    : { pathname: path.split('?')[0] };
  const str = query2Str(queryObj || {});
  const hasParams = !!str.length;
  return `${origin}${pathname}${hasParams ? `?${str}` : ''}`;
};

/**
 * assign url while replace params
 * @param { string } url
 * @param { object } queryObj object for custom params
 * @returns { string } new url
 */
export const assignUrl = (
  url: string,
  queryObj?: Record<string, unknown>
): string => {
  const { pathname, origin = '' } = isValidUrl(url)
    ? new URL(url)
    : { pathname: url };
  const mergedQuery: Record<string, unknown> = {
    ...parseQuery(url),
    ...(queryObj ?? {}),
  };
  return connectUrl(`${origin}${pathname}`, mergedQuery);
};

/**
 * 在新窗口打开url
 * @param url 需要跳转的url地址
 */
export function openLinkInNewWindow(url: string) {
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
